/**
 * Elite Realty 02 — Editorial grid, carded.
 *
 * No hero: the pictures arrive as three EQUAL frames, each lifted onto its own
 * shadowed card with a gold foot, and a ribbon crosses the first of them. The
 * dateline is the heaviest thing on the sheet, a dot field fills the margin
 * beside it, and the details sit in three ruled columns with disc icons.
 */

import { CANVAS, SPACE, RULE, RADIUS, SHADOW } from '../system/tokens';
import { getPalette, scrim } from '../system/palettes';
import { roles } from '../system/typography';
import { grid } from '../system/grid';
import { text, shape, rule, image, qrcode, page } from '../system/elements';
import { getIcon } from '../system/icons';
import { PHOTOS } from '../system/photos';
import { BRAND, SPECS, FEATURES } from './system';
import { card, iconDisc, dotGrid, hatch, ribbon, cornerBrackets } from './decor';

const ID = 'tpl-elite-flyer-02';

export default {
  id: ID,
  meta: {
    name: 'Elite — Open House',
    collection: 'elite-realty',
    archetype: 'editorial-three-column',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'elite-navy-gold',
    pairing: 'elite-signature',
    tags: ['real estate', 'flyer', 'open house', 'editorial', 'grid', 'magazine', 'bone', 'us letter'],
    description: 'An editorial three-column sheet with no hero: a heavy dateline, three equal photographs lifted onto carded frames, a ribbon, and ruled columns of details.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('elite-navy-gold');
    const r = roles('elite-signature');
    const g = grid(canvas, { margin: 165 });
    const els = [];

    const COL = Math.floor((g.contentWidth - SPACE.md * 2) / 3);
    const colX = (n) => g.left + n * (COL + SPACE.md);

    els.push(shape(`${ID}-field`, { x: 0, y: 0, width: canvas.width, height: canvas.height, fill: p.surface }));
    els.push(hatch(`${ID}-hatch`, {
      x: 0, y: 0, width: canvas.width, height: 26, color: p.accent, step: 22, weight: 6,
    }));

    // ── Masthead ────────────────────────────────────────────────────────────
    let y = 190;
    els.push(...iconDisc(`${ID}-logo`, {
      x: g.left, y, size: 88, content: getIcon('roofMark'), discFill: p.bg, iconColor: p.accent,
    }));
    els.push(text(`${ID}-logo-name`, r.h3, `${BRAND.name} ${BRAND.sub}`, { x: g.left + 116, y: y + 12, width: 760 },
      { color: p.surfaceInk, fontSize: 40, letterSpacing: 9 }));
    els.push(text(`${ID}-kicker`, r.statLabel, 'Open House · By appointment also', {
      x: g.left + 1240, y: y + 24, width: g.right - g.left - 1240, align: 'right',
    }, { color: p.surfaceInkMuted, fontSize: 30 }));
    y += 112;
    els.push(rule(`${ID}-mast-rule`, { x: g.left, y, width: g.contentWidth, weight: RULE.medium, color: p.surfaceInk }));
    y += RULE.medium + SPACE.lg;

    // ── Dateline, with a dot field filling the margin beside it ────────────
    els.push(dotGrid(`${ID}-dots`, {
      x: 1800, y: y + 20, width: 580, height: 400, color: scrim(p.surfaceInk, 0.26), step: 34, radius: 6,
    }));

    const date = text(`${ID}-date`, r.display, 'SAT 15\nJUNE', { x: g.left, y, width: 1560 },
      { color: p.surfaceInk, fontSize: 192 });
    els.push(date);
    y += date.height;

    els.push(text(`${ID}-script`, r.script, "You're invited", { x: g.left + 10, y, width: 1200 },
      { color: p.accentDeep, fontSize: 122, overlapOk: true }));
    y += 122 + SPACE.xs;

    els.push(...card(`${ID}-time`, {
      x: g.left, y, width: 700, height: 96, fill: p.bg, radius: RADIUS.sm, shadow: SHADOW.lift,
    }));
    els.push(text(`${ID}-time-copy`, r.cta, '11:00 AM – 2:00 PM', { x: g.left, y: y + 26, width: 700, align: 'center' },
      { color: p.accent, fontSize: 44 }));
    y += 96 + SPACE.lg;

    // ── Three equal frames, each on a card ─────────────────────────────────
    const FRAME_H = 560;
    [PHOTOS.timberDusk, PHOTOS.openPlanLiving, PHOTOS.brassBedroom].forEach((src, i) => {
      els.push(...card(`${ID}-fr${i}`, {
        x: colX(i) - SPACE.xxs, y: y - SPACE.xxs, width: COL + SPACE.xxs * 2, height: FRAME_H + SPACE.xxs * 2 + 30,
        fill: p.bg, radius: RADIUS.sm,
      }));
      els.push(image(`${ID}-frame${i}`, { x: colX(i), y, width: COL, height: FRAME_H, src, cornerRadius: RADIUS.sm }));
      els.push(shape(`${ID}-fr${i}-foot`, {
        x: colX(i) - SPACE.xxs, y: y + FRAME_H + SPACE.xxs, width: COL + SPACE.xxs * 2, height: 8, fill: p.accent,
      }));
    });
    els.push(...ribbon(`${ID}`, r, {
      x: colX(0) - 40, y: y + 150, width: COL + 80, height: 88,
      copy: 'Just listed', fill: p.accent, inkColor: p.accentInk, rotation: -7, fontSize: 40,
    }));
    y += FRAME_H + 30 + SPACE.md;

    ['Exterior, west elevation', 'Open-plan living', 'Primary bedroom'].forEach((cap, i) => {
      els.push(text(`${ID}-cap${i}`, r.caption, cap, { x: colX(i), y, width: COL },
        { color: p.surfaceInkMuted, fontSize: 28 }));
    });
    y += 28 * 1.4 + SPACE.md;

    els.push(rule(`${ID}-mid-rule`, { x: g.left, y, width: g.contentWidth, weight: RULE.hair, color: scrim(p.surfaceInk, 0.35) }));
    y += SPACE.md;

    // ── Three ruled columns ────────────────────────────────────────────────
    const bodyTop = y;
    els.push(text(`${ID}-c0-head`, r.statLabel, 'The address', { x: colX(0), y: bodyTop, width: COL },
      { color: p.accentDeep, fontSize: 30 }));
    els.push(text(`${ID}-c0-body`, r.body, `${BRAND.address[0]}\n${BRAND.address[1]}`, {
      x: colX(0), y: bodyTop + 62, width: COL,
    }, { color: p.surfaceInk, fontSize: 40 }));
    els.push(text(`${ID}-c0-price`, r.h3, '$789,000', { x: colX(0), y: bodyTop + 208, width: COL },
      { color: p.accentDeep, fontSize: 68 }));

    els.push(text(`${ID}-c1-head`, r.statLabel, 'The house', { x: colX(1), y: bodyTop, width: COL },
      { color: p.accentDeep, fontSize: 30 }));
    SPECS.forEach((s, i) => {
      const ry = bodyTop + 62 + i * 74;
      els.push(...iconDisc(`${ID}-spec${i}`, {
        x: colX(1), y: ry, size: 54, content: getIcon(s.icon), discFill: p.bg, iconColor: p.accent,
      }));
      els.push(text(`${ID}-spec${i}`, r.body, `${s.value}  ${s.label}`, {
        x: colX(1) + 74, y: ry + 6, width: COL - 74,
      }, { color: p.surfaceInk, fontSize: 40 }));
    });

    els.push(text(`${ID}-c2-head`, r.statLabel, 'Worth noting', { x: colX(2), y: bodyTop, width: COL },
      { color: p.accentDeep, fontSize: 30 }));
    FEATURES.slice(0, 6).forEach((f, i) => {
      const ry = bodyTop + 62 + i * 74;
      els.push(shape(`${ID}-feat${i}-dot`, {
        x: colX(2), y: ry + 16, width: 14, height: 14, kind: 'circle', fill: p.accent,
      }));
      els.push(text(`${ID}-feat${i}`, r.body, f, { x: colX(2) + 36, y: ry, width: COL - 36 },
        { color: p.surfaceInk, fontSize: 36 }));
    });

    // ── Navy colophon ──────────────────────────────────────────────────────
    const FOOT_H = 320;
    const FOOT_Y = canvas.height - FOOT_H;
    els.push(shape(`${ID}-foot`, { x: 0, y: FOOT_Y, width: canvas.width, height: FOOT_H, fill: p.bg }));
    els.push(hatch(`${ID}-foot-hatch`, {
      x: 0, y: FOOT_Y, width: canvas.width, height: 14, color: p.accent, step: 20, weight: 5,
    }));

    els.push(text(`${ID}-agent`, r.h3, BRAND.agent, { x: g.left, y: FOOT_Y + 78, width: 900 },
      { color: p.ink, fontSize: 52, letterSpacing: 3 }));
    els.push(text(`${ID}-agent-contact`, r.caption, `${BRAND.phone}  ·  ${BRAND.web}`, {
      x: g.left, y: FOOT_Y + 160, width: 1100,
    }, { color: p.inkMuted, fontSize: 32 }));

    els.push(...card(`${ID}-qr`, {
      x: g.right - 230, y: FOOT_Y + 70, width: 190, height: 190, fill: p.surface, radius: RADIUS.sm, shadow: SHADOW.lift,
    }));
    els.push(qrcode(`${ID}-qrcode`, {
      x: g.right - 215, y: FOOT_Y + 85, size: 160, data: BRAND.tourUrl, color: p.bg, bgColor: '#FFFFFF',
    }));
    els.push(text(`${ID}-qr-cap`, r.statLabel, 'Virtual tour', {
      x: g.right - 700, y: FOOT_Y + 148, width: 430, align: 'right',
    }, { color: p.accent, fontSize: 30 }));

    els.push(...cornerBrackets(`${ID}`, {
      x: 92, y: 92, width: canvas.width - 184, height: canvas.height - 184,
      arm: 140, weight: RULE.medium, color: scrim(p.accent, 0.9),
    }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.surface, elements: els })],
    };
  },
};
